import { useMemo } from "react";
import { Group } from "@/models/feature/feature";
import type { SendGroupMessageBody } from "@/services/groupService";
import type { LegislationSubscriptionCreateRequest } from "@/models/subscriptions/LegislationSubscription";
import type { LegislationSubscriptionRemoveRequest } from "@/models/subscriptions/LegislationSubscription";
import type { LegislationSubscription } from "@/models/subscriptions/LegislationSubscription";
import type { SubscriptionRequest } from "@/models/subscriptions/Subscription";
import { restError } from "@/models/client/RestResponse";
import { PATH_KEYS } from "../services/appServices";
import { useItemsCrud } from "./useItemsCrud";
import { useItemService } from "./useServices";
import { useMutation, useQuery, useQueries, useQueryClient } from "@tanstack/react-query";

const useGroups = () => {
  /**** Services ****/
  const queryClient = useQueryClient();

  const groupService = useItemService((services) => services.group);

  const lawTermCrud = useItemsCrud<Group>(
    [PATH_KEYS.group],
    (services) => services.group
  );

  /**** Queries & Mutations ****/
  /**
   * Every group in the tenant. Requires ReadGroups, so only call it from administrator screens.
   * Pass `enabled: false` from components that stay mounted while closed (LAR-456 follow-up).
   */
  const useGetGroups = ({ enabled = true }: { enabled?: boolean } = {}) =>
    useQuery<Group[]>({
      enabled,
      queryKey: [PATH_KEYS.group, "get"],
      queryFn: async () => {
        const response = await groupService.getGroups();
        if (response.success) return response.value;
        else throw restError(response);
      },
    });

  /**
   * The groups the signed-in user belongs to. Separate from {@link useGetGroups} because the
   * tenant-wide list is administrator-only: the features below run for every user on every
   * legislation and workspace table mount, so they 403'd ~1.5k times a day in production until
   * they asked for their own groups instead (LAR-456).
   */
  const useGetMyGroups = () =>
    useQuery<Group[]>({
      queryKey: [PATH_KEYS.group, "mine"],
      queryFn: async () => {
        const response = await groupService.getMyGroups();
        if (!response.success) throw restError(response);
        return response.value;
      },
    });

  const useGroupLegislationSubscriptions = (groupId: string | undefined) =>
    useQuery<LegislationSubscription[]>({
      queryKey: [PATH_KEYS.group, "legislation-subscriptions", groupId],
      queryFn: async () => {
        if (!groupId) return [];
        const response =
          await groupService.getGroupLegislationSubscriptions(groupId);
        if (response.success) return response.value;
        throw restError(response);
      },
      enabled: !!groupId,
    });

  /** Set of "datasource/externalId" for legislations that any of the current user's groups is subscribed to. */
  const useMyGroupsLegislationSubscriptionKeys = () => {
    const { data: groups } = useGetMyGroups();
    const myGroupIds = useMemo(() => groups?.map((g) => g.id) ?? [], [groups]);
    const results = useQueries({
      queries: myGroupIds.map((groupId) => ({
        queryKey: [PATH_KEYS.group, "legislation-subscriptions", groupId],
        queryFn: async (): Promise<LegislationSubscription[]> => {
          const res =
            await groupService.getGroupLegislationSubscriptions(groupId);
          return res.success ? res.value : [];
        },
      })),
    });
    const groupLegislationSubscriptionKeys = useMemo(() => {
      const set = new Set<string>();
      results.forEach((r) => {
        if (r.data)
          r.data.forEach(
            (sub: { datasource: string; externalId: string }) =>
              set.add(`${sub.datasource}/${sub.externalId}`)
          );
      });
      return set;
    }, [results]);
    const isLoading = results.some((r) => r.isLoading);
    return { groupLegislationSubscriptionKeys, isLoading };
  };

  /** Set of entityPaths (workspace, workspace-document) that any of the current user's groups is subscribed to. */
  const useMyGroupsSubscriptionEntityPaths = () => {
    const { data: groups } = useGetMyGroups();
    const myGroupIds = useMemo(() => groups?.map((g) => g.id) ?? [], [groups]);
    const results = useQueries({
      queries: myGroupIds.map((groupId) => ({
        queryKey: [PATH_KEYS.group, "subscriptions", groupId],
        queryFn: async (): Promise<{ entityPath: string }[]> => {
          const res = await groupService.getGroupSubscriptions(groupId, 1);
          if (!res.success) return [];
          return (res.value?.data ?? []).map((s) => ({ entityPath: s.entityPath }));
        },
      })),
    });
    const groupSubscriptionEntityPaths = useMemo(() => {
      const set = new Set<string>();
      results.forEach((r) => {
        if (r.data)
          r.data.forEach((s) => set.add(s.entityPath));
      });
      return set;
    }, [results]);
    const isLoading = results.some((r) => r.isLoading);
    return { groupSubscriptionEntityPaths, isLoading };
  };

  const removeMember = useMutation<
    string,
    string,
    {
      groupId: string;
      userId: string;
    }
  >({
    mutationKey: [PATH_KEYS.group, "removeMember"],
    mutationFn: async ({
      groupId,
      userId,
    }: {
      groupId: string;
      userId: string;
    }) => {
      const response = await groupService.removeMember(groupId, userId);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.group] });
    },
  });

  const sendMessage = useMutation<
    void,
    Error,
    { groupId: string; body: SendGroupMessageBody }
  >({
    mutationKey: [PATH_KEYS.group, "sendMessage"],
    mutationFn: async ({ groupId, body }) => {
      const response = await groupService.sendMessage(groupId, body);
      if (response.success) return;
      throw restError(response);
    },
  });

  const addGroupLegislationSubscription = useMutation({
    mutationKey: [PATH_KEYS.group, "addLegislationSubscription"],
    mutationFn: async ({
      groupId,
      body,
    }: {
      groupId: string;
      body: LegislationSubscriptionCreateRequest;
    }) => {
      const response =
        await groupService.addGroupLegislationSubscription(groupId, body);
      if (response.success) return response.value;
      throw restError(response);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.group] });
      queryClient.invalidateQueries({
        queryKey: [
          PATH_KEYS.group,
          "legislation-subscriptions",
          variables.groupId,
        ],
      });
    },
  });

  const removeGroupLegislationSubscription = useMutation({
    mutationKey: [PATH_KEYS.group, "removeLegislationSubscription"],
    mutationFn: async ({
      groupId,
      body,
    }: {
      groupId: string;
      body: LegislationSubscriptionRemoveRequest;
    }) => {
      const response =
        await groupService.removeGroupLegislationSubscription(groupId, body);
      if (response.success) return;
      throw restError(response);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.group] });
      queryClient.invalidateQueries({
        queryKey: [
          PATH_KEYS.group,
          "legislation-subscriptions",
          variables.groupId,
        ],
      });
    },
  });

  const addGroupSubscription = useMutation({
    mutationKey: [PATH_KEYS.group, "addSubscription"],
    mutationFn: async ({
      groupId,
      body,
    }: { groupId: string; body: SubscriptionRequest }) => {
      const response = await groupService.addGroupSubscription(groupId, body);
      if (response.success) return response.value;
      throw restError(response);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.group] });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.group, "subscriptions", variables.groupId],
      });
    },
  });

  const removeGroupSubscription = useMutation({
    mutationKey: [PATH_KEYS.group, "removeSubscription"],
    mutationFn: async ({
      groupId,
      body,
    }: { groupId: string; body: SubscriptionRequest }) => {
      const response = await groupService.removeGroupSubscription(
        groupId,
        body
      );
      if (response.success) return;
      throw restError(response);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.group] });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.group, "subscriptions", variables.groupId],
      });
    },
  });

  /**** Value ****/
  return {
    ...lawTermCrud,
    removeMember,
    sendMessage,
    useGetGroups,
    useGetMyGroups,
    useGroupLegislationSubscriptions,
    useMyGroupsLegislationSubscriptionKeys,
    useMyGroupsSubscriptionEntityPaths,
    addGroupLegislationSubscription,
    removeGroupLegislationSubscription,
    addGroupSubscription,
    removeGroupSubscription,
  };
};

export default useGroups;